import React, { useContext, useEffect, useState } from "react";
import { Drawer, Button, Typography, Space, Form, DatePicker } from "antd";
import { selectInterface } from "utilFunctions/shared/MultiSearchBar";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { UserInfoContext } from "Context/CommonContext";
import { DRS_STATUS } from "Pages/DRS/Constant/Constant";
import { DRS_INITIAL_DATE, useDRSContext } from "Pages/DRS/Context/DRSContext";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import MutlSelectAntdComponent from "utilFunctions/shared/AntdSharedComponents/MutlSelectAntdComponent";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { DRS_DASHBOARD_ACCESS_ROLE } from "utilFunctions/Constants/Constants";
export const INITIAL_STATUS = ["PENDING", "PICKED"];
const dateFormat = "YYYY/MM/DD";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export interface AdvancedFilterValues {
  search?: string;
  stream?: string;
  subject?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  dateRange?: any;
  questionId?: string;
  difficulty?: string;
}

interface AdvancedFiltersProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: AdvancedFilterValues) => void;
  onReset: () => void;
  form: any;
  type?: string;
}

import { Rate } from "antd";

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  open,
  onClose,
  onApply,
  onReset,
  form,
  type = "drsMyBucket",
}) => {
  const isDrsDashboard = type === "drsDashboard";
  console.log("Rendering AdvancedFilters with type:", type);
  const { screen }: any = useWindowSize();
  const {
    DSRFilters,
    drsDashboardFilters,
    individualFacultyFilters,
    drsAllSubjects,
  }: any = useDRSContext();
  const INITIAL_STATE_DRS = {
    statuses: INITIAL_STATUS,
    dateRange: [
      DRS_INITIAL_DATE.startDate
        ? dayjs(DRS_INITIAL_DATE.startDate, dateFormat)
        : null,
      DRS_INITIAL_DATE.endDate
        ? dayjs(DRS_INITIAL_DATE.endDate, dateFormat)
        : null,
    ],
  };
  const location = useLocation();
  const { pathname } = location;
  const isIndividualFaculty = pathname.includes("/DRS/faculty/");
  const currentFilters = isDrsDashboard
    ? isIndividualFaculty
      ? individualFacultyFilters
      : drsDashboardFilters
    : DSRFilters;

  const isSolvedByMe = currentFilters?.isSolvedByMe;

  const userInfo = useContext<IUserInfo>(UserInfoContext);

  const [stream, setStream] = useState<selectInterface[]>(
    userInfo?.userStreams ? userInfo?.userStreams : []
  );
  useEffect(() => {
    setStream(userInfo?.userStreams as any);
  }, [userInfo, userInfo?.userStreams]);

  useEffect(() => {
    if (open && currentFilters) {
      form.setFieldsValue({
        statuses: currentFilters.statuses,
        streamIds: currentFilters.streamIds,
        subjectIds: currentFilters.subjectIds,
        rating: currentFilters.rating,
        dateRange:
          !isDrsDashboard && currentFilters.dateRange?.startDate
            ? [
                dayjs(currentFilters.dateRange.startDate, dateFormat),
                dayjs(currentFilters.dateRange.endDate, dateFormat),
              ]
            : DRS_INITIAL_DATE,
        yearMonthStart: currentFilters.yearMonthStart
          ? [
              dayjs(currentFilters.yearMonthStart),
              dayjs(currentFilters.yearMonthEnd),
            ]
          : null,
      });
    }
  }, [open, currentFilters, form]);

  const handleApply = () => {
    form.validateFields().then((values: any) => {
      // Create a copy of values to manipulate
      const payload = { ...values };

      // Handle Date Range specifically for drsMyBucket
      if (!isDrsDashboard && values.dateRange) {
        // Flatten the object for the context
        payload.startDate = values.dateRange[0].format(dateFormat);
        payload.endDate = values.dateRange[1].format(dateFormat);
        delete payload.dateRange; // Remove the temporary array
      }

      if (!form.isFieldTouched("rating") && !values.rating) {
        delete payload.rating;
      }

      // If you specifically want to exclude it unless it has a truthy value:
      if (payload.rating === undefined || payload.rating === null) {
        delete payload.rating;
      }

      onApply(payload);
    });
  };
  return (
    <Drawer
      title={<Title level={3}>Advanced Filters</Title>}
      placement="right"
      width={["xs", "sm", "md"].includes(screen) ? "100%" : 420}
      onClose={onClose}
      open={open}
      styles={{
        body: { paddingBottom: 80 },
      }}
    >
      <Form form={form} layout="vertical" initialValues={INITIAL_STATE_DRS}>
        {/* Date Range */}
        {!isDrsDashboard && (
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker
              format={dateFormat}
              disabledDate={(current) => {
                // Disable future dates
                return current && current > dayjs().endOf("day");
              }}
              style={{ width: "100%" }}
              clearIcon={false}
            />
          </Form.Item>
        )}
        {/* Stream */}
        <Form.Item name="streamIds" label="Stream">
          <MutlSelectAntdComponent
            options={stream}
            placeholder="Select Stream"
            form={form}
            name={"streamIds"}
            maxTagCount={3}
          ></MutlSelectAntdComponent>
        </Form.Item>

        {/* Subject */}
        <Form.Item name="subjectIds" label="Subject">
          <MutlSelectAntdComponent
            options={
              isDrsDashboard &&
              DRS_DASHBOARD_ACCESS_ROLE.includes(userInfo?.fexRole || "")
                ? drsAllSubjects
                : userInfo?.subjectsListing
            }
            placeholder="Select Subject"
            form={form}
            name={"subjectIds"}
            maxTagCount={3}
          ></MutlSelectAntdComponent>
        </Form.Item>

        {/* Status */}
        {!isDrsDashboard && (
          <Form.Item name="statuses" label="Status">
            <MutlSelectAntdComponent
              options={DRS_STATUS}
              placeholder="Select Status"
              form={form}
              name={"statuses"}
              maxTagCount={3}
              disabled={isSolvedByMe}
            ></MutlSelectAntdComponent>
          </Form.Item>
        )}
        {isDrsDashboard && (
          <Form.Item label="Target Month" name="yearMonthStart">
            <RangePicker
              format={"MMM YY"}
              picker="month"
              minDate={dayjs().subtract(11, "months").startOf("month")}
              maxDate={dayjs().endOf("month")}
              size="large"
              style={{ width: "100%" }}
              allowClear={false}
            />
          </Form.Item>
        )}
        {/* Rating - Only show for My Bucket */}
        {!isDrsDashboard && (
          <Form.Item name="rating" label="Rating">
            <Rate
              allowClear={true}
              // If the form value is empty, the UI will show 0 stars (unselected)
              value={form.getFieldValue("rating")}
              onChange={(value) => {
                // This only sets the value when the user actually clicks a star
                form.setFieldsValue({ rating: value });
              }}
            />
          </Form.Item>
        )}
      </Form>

      {/* Footer Buttons */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "16px",
          borderTop: "1px solid #eee",
          background: "#fff",
        }}
      >
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button onClick={onReset}>Reset</Button>
          <Button type="primary" onClick={handleApply}>
            Apply Filters
          </Button>
        </Space>
      </div>
    </Drawer>
  );
};

export default AdvancedFilters;
